import { useEffect, useState } from "react";

const CACHE_PREFIX = "stemx365_page_";

const getCacheKey = slug => `${CACHE_PREFIX}${slug}`;

const readCache = slug => {
    try {
        const cached = localStorage.getItem(getCacheKey(slug));
        return cached ? JSON.parse(cached) : [];
    } catch (error) {
        console.error("Cache read error:", error);
        return [];
    }
};

const saveCache = (slug, data) => {
    try {
        localStorage.setItem(
            getCacheKey(slug),
            JSON.stringify(data)
        );
    } catch (error) {
        console.error("Cache save error:", error);
    }
};

const usePageContent = slug => {
    const [sections, setSections] = useState(() =>
        slug ? readCache(slug) : []
    );

    const [loading, setLoading] = useState(!sections.length);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!slug) return;

        let cancelled = false;

        const cached = readCache(slug);

        if (cached.length) {
            setSections(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }

        setError(null);

        fetch(`/api/pages/${encodeURIComponent(slug)}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error(
                        `Failed to load page: ${response.status}`
                    );
                }

                return response.json();
            })
            .then(data => {
                if (cancelled) return;

                setSections(data);
                saveCache(slug, data);
                setError(null);
            })
            .catch(err => {
                if (cancelled) return;

                console.error(err);

                if (!cached.length) {
                    setError(
                        err.message || "Failed to load page"
                    );
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [slug]);

    /*
     * Backend structure:
     *
     * sections
     *   └── outer section
     *       └── data[]
     *           └── section
     *               └── data
     */

    const pageSections = sections.flatMap(section => {
        if (Array.isArray(section.data)) {
            return section.data;
        }

        return [section];
    });

    const getSection = key => {
        return pageSections.find(
            section => section.section_key === key
        );
    };

    const get = key => {
        const section = getSection(key);

        //console.log("GET SECTION:", key, section);

        return section?.data || {};
    };

    const refresh = async () => {
        if (!slug) return;

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `/api/pages/${encodeURIComponent(slug)}`
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to load page: ${response.status}`
                );
            }

            const data = await response.json();

            setSections(data);
            saveCache(slug, data);

            return data;
        } catch (err) {
            console.error(err);
            setError(
                err.message || "Failed to load page"
            );
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const clearCache = () => {
        if (!slug) return;

        localStorage.removeItem(getCacheKey(slug));
        setSections([]);
    };

    return {
        sections,
        pageSections,
        get,
        getSection,
        loading,
        error,
        refresh,
        clearCache
    };
};

export default usePageContent;